import React, { useEffect } from "react";
import { Link, NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { clearAdminSession, isAdminAuthenticated } from "../../utils/adminSession";
import { clearAdminApiToken } from "../../utils/adminApiToken";
import { store } from "../../store/store";
import { pushToast } from "../../store/slices/toastSlice";
import { isAdminApiEnabled } from "../../config/adminApiFeature";
import { useAppSelector } from "../../store/hooks";
import VizhacksLogoMark from "../../components/VizhacksLogoMark";
import AdminTopBar from "./AdminTopBar";
import "./admin-dashboard.css";

function SidebarNavItem({ to, end, children, badge }) {
  const n = typeof badge === "number" ? badge : 0;
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `admin-dash__nav-item${isActive ? " admin-dash__nav-item--active" : ""}`
      }
    >
      <span className="admin-dash__nav-row">
        <span className="admin-dash__nav-label">{children}</span>
        {n > 0 ? (
          <span className="admin-dash__nav-badge" aria-label={`${n} unread`}>
            {n > 99 ? "99+" : n}
          </span>
        ) : null}
      </span>
    </NavLink>
  );
}

export default function AdminLayout() {
  const navigate = useNavigate();
  const apiOn = isAdminApiEnabled();
  const dashboard = useAppSelector((s) => s.dashboard);

  const unreadRequests = apiOn ? (dashboard.data?.unread?.requests ?? 0) : 0;
  const unreadContacts = apiOn ? (dashboard.data?.unread?.contacts ?? 0) : 0;

  useEffect(() => {
    function onApiUnauthorized() {
      clearAdminApiToken();
      if (isAdminApiEnabled()) {
        store.dispatch(
          pushToast({
            variant: "error",
            title: "API unauthorized",
            message:
              "The server returned 401. Check REACT_APP_ADMIN_BEARER_TOKEN or sign in again.",
          })
        );
      }
    }
    window.addEventListener("vh-admin-api-unauthorized", onApiUnauthorized);
    return () =>
      window.removeEventListener("vh-admin-api-unauthorized", onApiUnauthorized);
  }, []);

  if (!isAdminAuthenticated()) {
    return <Navigate to="/admin" replace />;
  }

  function handleSignOut() {
    clearAdminSession();
    clearAdminApiToken();
    navigate("/admin", { replace: true });
  }

  return (
    <div className="admin-dash">
      <aside className="admin-dash__sidebar" aria-label="Admin navigation">
        <Link className="admin-dash__brand" to="/admin/dashboard" title="Dashboard home">
          <VizhacksLogoMark className="admin-dash__logo-mark" height={24} aria-label="Vizhacks" />
          <span className="admin-dash__brand-sub">Admin</span>
        </Link>
        <nav className="admin-dash__nav" aria-label="Sections">
          <SidebarNavItem to="/admin/dashboard" end>
            Overview
          </SidebarNavItem>
          <SidebarNavItem to="/admin/dashboard/requests" badge={unreadRequests}>
            Service requests
          </SidebarNavItem>
          <SidebarNavItem to="/admin/dashboard/contact" badge={unreadContacts}>
            Contact messages
          </SidebarNavItem>
          <SidebarNavItem to="/admin/dashboard/email-analysis" badge={unreadRequests}>
            Email analyser
          </SidebarNavItem>
          <Link className="admin-dash__nav-item admin-dash__nav-item--external" to="/mdwriter">
            Markdown editor
          </Link>
        </nav>

        <div className="admin-dash__sidebar-foot">
          <button type="button" className="admin-dash__btn-ghost" onClick={handleSignOut}>
            Sign out
          </button>
          <Link className="admin-dash__link-subtle" to="/">
            ← Public site
          </Link>
        </div>
      </aside>
      <div className="admin-dash__main">
        <AdminTopBar />
        <div className="admin-dash__main-inner">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
